import React, { useState, useEffect } from 'react';
import { Question as QuestionType } from '../../types';
import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  TextField,
} from '@mui/material';
export interface QuestionProps {
  question: QuestionType;
  callback: (value: string, question: QuestionType) => void;
  currentValue?: string;
}

export const Question = (props: QuestionProps): JSX.Element => {
  const [showOther, setShowOther] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>(
    props.currentValue
      ? props.question.kind === 'multi-select'
        ? props.currentValue?.split(';')
        : [...props.currentValue]
      : []
  );

  const handleOtherValue = (value: string | undefined) => {
    if (!value) {
      setShowOther(!showOther);
    }
    if (props.question.kind === 'multi-select') {
      handleMultiSelect(`other: ${value}`, true);
    } else {
      handleSingleSelect(`other: ${value}`);
    }
  };

  const handleMultiSelect = (value: string, isOther: boolean = false) => {
    if (isOther) {
      const updated =
        value === 'other: undefined'
          ? [...selected.filter((i) => !i.startsWith('other:'))]
          : [...selected.filter((i) => !i.startsWith('other:')), value];
      setSelected(updated);
      props.callback(updated.join(';'), props.question);
    } else {
      const valueIdx = selected.indexOf(value);
      const updated =
        valueIdx === -1
          ? [...selected, value]
          : [...selected.filter((i) => i !== value)];
      setSelected(updated);
      props.callback(updated.join(';'), props.question);
    }
  };

  const handleTextChange = (value: string) => {
    props.callback(value, props.question);
  };

  const handleSingleSelect = (value: string) => {
    props.callback(value, props.question);
  };

  const textField = (): JSX.Element => {
    return (
      <TextField
        onChange={(e) => handleTextChange(e.target.value)}
        defaultValue={selected[0]}
      />
    );
  };

  const otherOption = (): JSX.Element => {
    return (
      <>
        <ListItem
          sx={{
            borderRadius: 2,
            mb: 1,
            backgroundColor: showOther ? 'info.main' : 'white',
            color: showOther ? 'white' : 'black',
            cursor: 'pointer',
          }}
        >
          <ListItemText
            sx={{ minWidth: 400 }}
            onClick={() => handleOtherValue(undefined)}
          >
            Other
          </ListItemText>
        </ListItem>
        {showOther && (
          <TextField
            sx={{ width: '100%' }}
            onChange={(e) => handleOtherValue(e.target.value)}
          />
        )}
      </>
    );
  };

  const singleSelectField = (): JSX.Element => {
    return (
      <List sx={{ width: '90%' }}>
        {props.question.data?.options.map((option, idx: number) => {
          return (
            <ListItem
              key={idx}
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: Array.from(selected).includes(option.value)
                  ? 'info.main'
                  : 'white',
                color: Array.from(selected).includes(option.value)
                  ? 'white'
                  : 'black',
                cursor: 'pointer',
              }}
              onClick={() => handleSingleSelect(option.value)}
            >
              <ListItemText
                sx={{ minWidth: 400 }}
                primary={option.title}
                secondary={option.subtitle}
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  const multiSelectField = (): JSX.Element => {
    return (
      <List>
        {props.question.data?.options.map((option, idx: number) => {
          return (
            <ListItem
              key={idx}
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: Array.from(selected).includes(option.value)
                  ? 'info.main'
                  : 'white',
                color: Array.from(selected).includes(option.value)
                  ? 'white'
                  : 'black',
                cursor: 'pointer',
              }}
              onClick={() => handleMultiSelect(option.value)}
            >
              <ListItemText
                sx={{ minWidth: 400 }}
                primary={option.title}
                secondary={option.subtitle}
              />
            </ListItem>
          );
        })}
        {props.question.include_other && otherOption()}
      </List>
    );
  };

  const numberField = (): JSX.Element => {
    return (
      <TextField
        type="number"
        defaultValue={selected[0]}
        onChange={(e) => props.callback(e.target.value, props.question)}
      />
    );
  };

  const componentMapping: Record<string, () => void> = {
    text: textField,
    'single-select': singleSelectField,
    'multi-select': multiSelectField,
    number: numberField,
  };

  return (
    <>
      <Grid container justifyContent="center">
        <Typography variant="bold" sx={{ textAlign: 'center', py: 1 }}>
          {props.question.title}
        </Typography>
        <Typography sx={{ textAlign: 'center' }}>
          {props.question.subtitle}
        </Typography>
      </Grid>
      <Grid container justifyContent="center">
        {componentMapping[props.question.kind]()}
      </Grid>
    </>
  );
};
